import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getPlans();
      setPlans(res.data.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const openCreatePage = () => {
    navigate('/plans/create');
  };

  const openEditPage = (plan) => {
    navigate(`/plans/edit/${plan._id}`, { state: { plan } });
  };

  const toggleStatus = async (plan) => {
    try {
      await adminApi.updatePlan(plan._id, { isActive: !plan.isActive });
      toast.success(`Plan ${plan.isActive ? 'deactivated' : 'activated'}`);
      fetchPlans();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title="SaaS Plans Management"
        subtitle="Configure pricing tiers, features, and limits."
        actionButton={
          <button 
            onClick={openCreatePage}
            className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-sm transition-all transform hover:-translate-y-0.5 hover:shadow-md"
          >
            + Create New Plan
          </button>
        }
      />

      <main className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full p-12 text-center text-gray-500 font-bold">Loading plans...</div>
          ) : plans.length === 0 ? (
            <div className="col-span-full p-12 text-center text-gray-500 font-bold bg-white rounded-2xl shadow-sm border border-gray-100">
              No plans found. Create one to get started.
            </div>
          ) : (
            plans.map(plan => (
              <div key={plan._id} className="bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-8 border-t-[6px] border-red-600 hover:-translate-y-1 transition-transform relative overflow-hidden group flex flex-col">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-2xl font-extrabold text-gray-900 uppercase tracking-tight">{plan.name}</h3>
                  <span className={`px-3 py-1 text-xs font-bold rounded-full border ${plan.isActive ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                    {plan.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 font-medium h-10">{plan.description}</p>
                
                <div className="mt-5 mb-6">
                  <span className="text-4xl font-extrabold text-gray-900">{plan.price?.currency || '₹'}{plan.price?.monthly || 0}</span>
                  <span className="text-base font-bold text-gray-400">/mo</span>
                  <div className="text-xs font-medium text-gray-400 mt-1">or {plan.price?.currency || '₹'}{plan.price?.yearly || 0} /year</div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-sm">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-gray-600 font-medium">Businesses: <span className="font-bold text-gray-900">{plan.limits?.maxBusinesses === -1 ? 'Unlimited' : plan.limits?.maxBusinesses}</span></span>
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-gray-600 font-medium">Staff Limit: <span className="font-bold text-gray-900">{plan.limits?.maxStaff === -1 ? 'Unlimited' : plan.limits?.maxStaff}</span></span>
                  </li>
                  <li className="flex items-center text-sm">
                    {plan.features?.aiAssist ? <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> : <XCircleIcon className="h-5 w-5 text-gray-300 mr-2" />}
                    <span className={`font-medium ${plan.features?.aiAssist ? 'text-gray-900' : 'text-gray-400'}`}>AI Assistant</span>
                  </li>
                  <li className="flex items-center text-sm">
                    {plan.features?.whatsapp ? <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> : <XCircleIcon className="h-5 w-5 text-gray-300 mr-2" />}
                    <span className={`font-medium ${plan.features?.whatsapp ? 'text-gray-900' : 'text-gray-400'}`}>WhatsApp Integration</span>
                  </li>
                  <li className="flex items-center text-sm">
                    {plan.features?.apiAccess ? <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> : <XCircleIcon className="h-5 w-5 text-gray-300 mr-2" />}
                    <span className={`font-medium ${plan.features?.apiAccess ? 'text-gray-900' : 'text-gray-400'}`}>API Access</span>
                  </li>
                </ul>
                
                <div className="flex space-x-3 mt-auto pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => openEditPage(plan)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2.5 rounded-full text-sm font-bold transition-colors"
                  >
                    Edit Plan
                  </button>
                  <button 
                    onClick={() => toggleStatus(plan)}
                    className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${plan.isActive ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                  >
                    {plan.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default Plans;
